import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const lines = [
    { text: "Every insurer.", cls: "text-ink" },
    { text: "One advisor.", cls: "text-ink" },
    { text: "Zero confusion.", cls: "italic text-clay" },
];

const HERO_IMG =
    "https://images.unsplash.com/photo-1474540412665-1cdae210ae6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG5hdHVyZSUyMHdhdGVyJTIwY2FsbXxlbnwwfHx8fDE3ODU5MzY4MzZ8MA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
    const { scrollY } = useScroll();
    const imgY = useTransform(scrollY, [0, 900], [0, 140]);

    return (
        <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-32 md:pt-40">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 md:px-12 lg:grid-cols-12 lg:gap-8">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        data-testid="hero-overline"
                        className="mb-10 font-mono text-xs uppercase tracking-[0.25em] text-moss"
                    >
                        PolicyOne — Insurance Advisory · Coimbatore, Tamil Nadu
                    </motion.p>
                    <h1 data-testid="hero-headline" className="font-serif text-5xl font-medium leading-[0.95] tracking-tighter sm:text-7xl lg:text-8xl">
                        {lines.map((l, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 1.1, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                                    className={`block ${l.cls}`}
                                >
                                    {l.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.8 }}
                        data-testid="hero-subtext"
                        className="mt-10 max-w-md text-base leading-relaxed text-moss md:text-lg"
                    >
                        We compare policies from 40+ IRDAI-registered insurers and recommend the one that actually fits your life — explained in plain Tamil and English, at zero cost to you.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.95 }}
                        className="mt-12 flex flex-wrap items-center gap-6"
                    >
                        <a
                            href="#quote"
                            data-testid="hero-cta-quote"
                            className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-clay-dark"
                        >
                            Get Free Advice
                            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                        <a
                            href="#coverages"
                            data-testid="hero-cta-explore"
                            className="font-mono text-xs uppercase tracking-[0.2em] text-ink underline decoration-line underline-offset-8 transition-colors duration-300 hover:decoration-clay"
                        >
                            Explore Coverages
                        </a>
                    </motion.div>
                </div>
                <div className="relative lg:col-span-5">
                    <motion.div
                        initial={{ clipPath: "inset(100% 0 0 0)" }}
                        animate={{ clipPath: "inset(0% 0 0 0)" }}
                        transition={{ duration: 1.3, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="relative h-[420px] overflow-hidden lg:h-[560px]"
                    >
                        <motion.img
                            src={HERO_IMG}
                            alt="Calm waters — the peace of mind PolicyOne stands for"
                            data-testid="hero-image"
                            style={{ y: imgY }}
                            className="h-[120%] w-full scale-110 object-cover"
                        />
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.4 }}
                        className="mt-4 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-moss"
                    >
                        <span>Est. 2008 — Sundarapuram</span>
                        <span className="flex items-center gap-2">
                            Scroll <ArrowDown size={12} />
                        </span>
                    </motion.p>
                </div>
            </div>
        </section>
    );
};
